import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { scaleQuantile, scaleThreshold } from 'd3-scale';
import { cn } from '../../lib/utils';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const STATE_ABBR = {
  Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',Colorado:'CO',Connecticut:'CT',Delaware:'DE',Florida:'FL',Georgia:'GA',Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',Kentucky:'KY',Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',Michigan:'MI',Minnesota:'MN',Mississippi:'MS',Missouri:'MO',Montana:'MT',Nebraska:'NE',Nevada:'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',Utah:'UT',Vermont:'VT',Virginia:'VA',Washington:'WA','West Virginia':'WV',Wisconsin:'WI',Wyoming:'WY','District of Columbia':'DC',
};

// Three themes: light (clean white), dark (deep navy → bright royal blue
// ramp, matching the broker's reference), paper (warm cream — Carta-style).
const HEAT_LIGHT = ['#f3f6fc', '#dbe6fa', '#a7c2f2', '#5e8be4', '#2d5fc9'];
// Same blue family as the light ramp but tuned for the #0a0a0a canvas —
// the muted base lifts above the background, the top punches royal-blue.
const HEAT_DARK  = ['#0f172a', '#1e3a8a', '#2563eb', '#3b82f6', '#7dafff'];
// Restrained sage ramp on warm cream — escalates from empty cream to deep forest
const HEAT_PAPER = ['#f1ede3', '#dfdbcc', '#bbc5a8', '#8aa181', '#5d7553'];

// Color-code response rate consistently with the dashboard tables:
//   ≥ 0.75%        green
//   0.50% – 0.74%  amber
//   < 0.50%        red
function respRateClass(v) {
  const n = Number(v || 0);
  if (n >= 0.75) return 'text-emerald-500';
  if (n >= 0.5) return 'text-amber-500';
  return 'text-red-500';
}

/**
 * Embeddable US heatmap.
 *
 * Props:
 *  - rows: [{ state, total_mailed, responses, submissions, funded, response_rate, funded_rate, ... }]
 *  - metric: which row field to drive color
 *  - height: pixel height of the map area
 *  - theme: 'light' | 'dark' | 'paper'
 */
export default function StateHeatmap({ rows, metric = 'response_rate', height = 360, theme = 'light' }) {
  const [hover, setHover] = useState(null);
  const isDark = theme === 'dark';
  const isPaper = theme === 'paper';
  const HEAT = isDark ? HEAT_DARK : (isPaper ? HEAT_PAPER : HEAT_LIGHT);
  const STROKE = isDark ? '#0f1626' : (isPaper ? '#faf8f3' : '#ffffff');
  const HOVER_FILL = isDark ? '#34d399' : (isPaper ? '#3f5240' : 'hsl(var(--kf-gold))');

  const dataMap = useMemo(() => Object.fromEntries(rows.map((r) => [r.state, r])), [rows]);
  // Response-rate uses fixed thresholds aligned with the dashboard tables
  // (≥1.00% / 0.75–0.99% / 0.50–0.74% / 0.25–0.49% / <0.25%). Other metrics
  // fall back to a 5-quantile scale derived from the data.
  const RESP_THRESHOLDS = [0.25, 0.5, 0.75, 1.0];
  const colorScale = useMemo(() => {
    if (metric === 'response_rate') {
      return scaleThreshold().domain(RESP_THRESHOLDS).range(HEAT);
    }
    const values = rows.map((r) => r[metric] || 0).filter((v) => v > 0);
    if (!values.length) return () => HEAT[0];
    return scaleQuantile().domain(values).range(HEAT);
  }, [rows, metric, HEAT]);

  return (
    <div className="relative">
      <div style={{ height }} data-testid="dashboard-state-heatmap">
        <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 1000 }} className="w-full h-full">
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map((geo) => {
              const abbr = STATE_ABBR[geo.properties.name];
              const d = dataMap[abbr];
              const val = d ? d[metric] || 0 : 0;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={val > 0 ? colorScale(val) : HEAT[0]}
                  stroke={STROKE}
                  strokeWidth={isDark ? 1 : 0.7}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none', fill: HOVER_FILL, cursor: 'pointer' },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={() => setHover({ name: geo.properties.name, abbr, data: d })}
                  onMouseLeave={() => setHover(null)}
                />
              );
            })}
          </Geographies>
        </ComposableMap>
      </div>
      {hover && hover.data && (
        <div className={cn(
          'absolute top-2 right-2 rounded-xl p-3 shadow-2xl text-[12px] min-w-[200px] z-10 backdrop-blur',
          isDark
            ? 'bg-[#141c33]/95 border border-white/10 text-white'
            : 'bg-white border hairline text-neutral-900',
        )}>
          <div className={cn('font-bold mb-1.5 display tracking-tight', isDark ? 'text-white' : 'text-neutral-900')}>{hover.name}</div>
          <div className={cn('space-y-0.5', isDark ? 'text-[#7a89b2]' : 'text-neutral-600')}>
            <div className="flex justify-between gap-4"><span>Mailed</span><span className={cn('display font-bold', isDark ? 'text-white' : 'text-neutral-900')}>{(hover.data.total_mailed || 0).toLocaleString()}</span></div>
            <div className="flex justify-between gap-4"><span>Responded</span><span className="display font-bold text-[#60a5fa]">{hover.data.responses || 0}</span></div>
            <div className="flex justify-between gap-4"><span>Submissions</span><span className="display font-bold text-[#fb923c]">{hover.data.submissions || 0}</span></div>
            <div className="flex justify-between gap-4"><span>Funded</span><span className="display font-bold text-[#a78bfa]">{hover.data.funded || 0}</span></div>
            <div className={cn('flex justify-between gap-4 pt-1 mt-1 border-t', isDark ? 'border-white/10' : 'border-neutral-100')}>
              <span>Resp Rate</span>
              <span className={cn('display font-bold', respRateClass(hover.data.response_rate))}>{(hover.data.response_rate || 0).toFixed(2)}%</span>
            </div>
          </div>
        </div>
      )}
      {/* Legend */}
      <div className={cn(
        'absolute bottom-3 right-3 rounded-lg p-2.5 text-[10px] backdrop-blur',
        isDark ? 'bg-[#141c33]/85 border border-white/[0.06]' : 'bg-white/85 border hairline',
      )}>
        {metric === 'response_rate' ? (
          <>
            <div className={cn('text-[9.5px] uppercase tracking-wider font-bold mb-1.5', isDark ? 'text-[#7a89b2]' : 'text-neutral-500')}>Response Rate</div>
            {[
              { label: '1.00% or higher', color: HEAT[4] },
              { label: '0.75% – 0.99%',  color: HEAT[3] },
              { label: '0.50% – 0.74%',  color: HEAT[2] },
              { label: '0.25% – 0.49%',  color: HEAT[1] },
              { label: 'Below 0.25%',    color: HEAT[0] },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-1.5 mt-0.5">
                <span className="h-2.5 w-4 rounded-sm" style={{ background: row.color, border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e5e5' }} />
                <span className={cn('font-semibold', isDark ? 'text-[#94a3c2]' : 'text-neutral-600')}>{row.label}</span>
              </div>
            ))}
          </>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className={cn('text-[9.5px] uppercase tracking-wider font-bold', isDark ? 'text-[#7a89b2]' : 'text-neutral-500')}>Low</span>
            {HEAT.map((c, i) => (
              <span key={i} className="h-2.5 w-5 rounded-sm" style={{ background: c, border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e5e5' }} />
            ))}
            <span className={cn('text-[9.5px] uppercase tracking-wider font-bold', isDark ? 'text-[#7a89b2]' : 'text-neutral-500')}>High</span>
          </div>
        )}
      </div>
    </div>
  );
}
